import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useDepollution from "../../data/useDepollution";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import openInNewTab from "../../utils/openInNewTab";

type DepollutionButtonProps = {
  vehicleId: string;
  depollutionId?: string | null;
  openInNewTab?: boolean;
  navigateOnCreate?: boolean;
  classes?: string;
};

export default function DepollutionButton({
  vehicleId,
  depollutionId,
  openInNewTab: shouldOpenInNewTab = true,
  navigateOnCreate = true,
  classes = "",
}: DepollutionButtonProps) {
  const { t } = useTranslation("vehicles");
  const navigate = useNavigate();
  const depollutionLabel = t("Depollution");
  const createLabel = t("Depollute");
  const { saveHandlers } = useDefaultCRUDHandlers(depollutionLabel);
  const [activeDepollutionId, setActiveDepollutionId] = useState<string | undefined>(depollutionId ?? undefined);
  const { depollution, createDepollution } = useDepollution(activeDepollutionId, vehicleId);

  useEffect(() => {
    setActiveDepollutionId(depollutionId ?? undefined);
  }, [depollutionId]);

  const hasDepollution = !!activeDepollutionId;
  const isDepollutionComplete = !!depollution.data?.data?.completed;

  const openDepollution = (depollutionId: string) => {
    const path = `/depollution/${depollutionId}`;
    if (shouldOpenInNewTab) {
      openInNewTab(path);
      return;
    }

    navigate(path);
  };

  const handleDepollute = () => {
    createDepollution.mutate(
      { vehicleId },
      {
        onSuccess: (res) => {
          setActiveDepollutionId(res.data.id);
          saveHandlers.onSuccess();
          if (navigateOnCreate) {
            openDepollution(res.data.id);
          }
        },
        onError: saveHandlers.onError,
      }
    );
  };

  if (hasDepollution) {
    return (
      <ButtonInput
        label={isDepollutionComplete ? t("{{depollutionLabel}} Complete", { depollutionLabel }) : t("View {{depollutionLabel}}", { depollutionLabel })}
        onClick={() => openDepollution(activeDepollutionId!)}
        classes={classes}
      />
    );
  }

  return (
    <ButtonInput
      label={createLabel}
      onClick={handleDepollute}
      isLoading={createDepollution.isLoading}
      classes={classes}
    />
  );
}
